import { useFormik, Form, FormikProvider } from 'formik';
import ReactSelect from 'react-select';
import { Button } from 'styled';
import InputField from 'components/formik/InputField';
import Modal from 'components/Modal';
import { getAllData, updateDocumentByID } from 'apis/customApiHandler';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';

const selectStyles = {
  control: (base) => ({
    ...base,
    borderColor: 'transparent',
    minWidth: '100%',
    '&:hover': {
      borderColor: 'rgb(34 197 94)',
    },
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

const PEOPLE_FIELDS = [
  {
    name: 'hubInCharges',
    naName: 'hubInChargeNA',
    usersKey: 'hubInChargeUsers',
    label: 'Hub In-Charge',
  },
  {
    name: 'fleetManagers',
    naName: 'fleetManagerNA',
    usersKey: 'fleetManagerUsers',
    label: 'Fleet Manager',
  },
  {
    name: 'clusterManagers',
    naName: 'clusterManagerNA',
    usersKey: 'clusterManagerUsers',
    label: 'Cluster Manager',
  },
  { name: 'zms', naName: 'zmNA', usersKey: 'zmUsers', label: 'Zonal Manager' },
];

const NA_VALUE = '__NA__';

const toIdList = (value) => (value || []).map((item) => String(item));

const Edit = ({ data, handleClick }) => {
  const queryClient = useQueryClient();

  const { data: filterOptions } = useQuery(['fleet-hub-mapping-options'], () =>
    getAllData('/fleet-hub-mapping/filter-options')
  );

  const { data: users } = useQuery(['users'], () => getAllData('/user'));

  const bodyTypes = filterOptions?.data?.bodyTypes || [];

  const userOptions = (users?.data || []).map((user) => ({
    value: String(user._id),
    label: user.name,
  }));

  const inactiveAssignedOptions = PEOPLE_FIELDS.flatMap(
    (field) => data[field.usersKey] || []
  )
    .filter((user) => user.active === false)
    .map((user) => ({
      value: String(user._id),
      label: `${user.name} (Inactive)`,
    }));

  const optionByValue = new Map(
    [...userOptions, ...inactiveAssignedOptions].map((option) => [
      option.value,
      option,
    ])
  );

  const selectableOptions = [...optionByValue.values()];

  const formik = useFormik({
    initialValues: {
      fleetCode: data.fleetCode,
      fleetName: data.fleetName,
      hubName: data.hubName,
      bodyType: data.bodyTypeId ? String(data.bodyTypeId) : '',
      hubInCharges: toIdList(data.hubInChargeIds),
      fleetManagers: toIdList(data.fleetManagerIds),
      clusterManagers: toIdList(data.clusterManagerIds),
      zms: toIdList(data.zmIds),
      hubInChargeNA: !!data.hubInChargeNA,
      fleetManagerNA: !!data.fleetManagerNA,
      clusterManagerNA: !!data.clusterManagerNA,
      zmNA: !!data.zmNA,
    },
    onSubmit: async (values) => {
      const hubResult = await updateDocumentByID(
        'fleet-hub-mapping/hub',
        data._id,
        {
          bodyType: values.bodyType || null,
          hubInCharges: values.hubInCharges,
          fleetManagers: values.fleetManagers,
          clusterManagers: values.clusterManagers,
          zms: values.zms,
          hubInChargeNA: values.hubInChargeNA,
          fleetManagerNA: values.fleetManagerNA,
          clusterManagerNA: values.clusterManagerNA,
          zmNA: values.zmNA,
        }
      );
      if (!hubResult) return;

      toast.success('Successfully updated');
      queryClient.invalidateQueries('fleet-hub-mapping');
      queryClient.invalidateQueries('fleet-hub-mapping-options');
      handleClick();
    },
  });

  const selectedBodyType = bodyTypes.find(
    (item) => String(item._id) === formik.values.bodyType
  );

  return (
    <Modal
      handleClick={handleClick}
      title="Edit Body Type / Hub In-Charge / Fleet Manager / Cluster Manager / Zonal Manager"
      className="max-w-2xl min-h-fit"
    >
      <FormikProvider value={formik}>
        <Form className="px-5 py-5 text-gray-700 font-medium text-base grid lg:gap-x-14 gap-y-5 grid-flow-row grid-cols-1 lg:grid-cols-2 w-full">
          <InputField
            type="text"
            label="Fleet Code"
            name="fleetCode"
            desable={true}
          />
          <InputField
            type="text"
            label="Fleet Name"
            name="fleetName"
            desable={true}
          />
          <InputField
            type="text"
            label="Hub Name"
            name="hubName"
            desable={true}
          />
          <label className="flex flex-col gap-0 text-gray-600 font-normal">
            Body Type
            <ReactSelect
              styles={selectStyles}
              menuPortalTarget={document.body}
              className="shadow-md rounded-md py-1 mb-2"
              options={[
                { value: '', label: 'None' },
                ...bodyTypes.map((item) => ({
                  value: String(item._id),
                  label: item.name,
                })),
              ]}
              value={{
                value: formik.values.bodyType,
                label: selectedBodyType?.name || 'None',
              }}
              onChange={(option) =>
                formik.setFieldValue('bodyType', option?.value || '')
              }
              name="bodyType"
              isMulti={false}
            />
          </label>
          {PEOPLE_FIELDS.map((field) => (
            <label
              key={field.name}
              className="flex flex-col gap-0 text-gray-600 font-normal"
            >
              {field.label}
              <ReactSelect
                styles={selectStyles}
                menuPortalTarget={document.body}
                className="shadow-md rounded-md py-1 mb-2"
                options={[
                  { value: NA_VALUE, label: 'N/A' },
                  ...selectableOptions,
                ]}
                value={
                  formik.values[field.naName]
                    ? [{ value: NA_VALUE, label: 'N/A' }]
                    : formik.values[field.name].map((userId) => ({
                        value: userId,
                        label: optionByValue.get(String(userId))?.label,
                      }))
                }
                onChange={(selected) => {
                  const picked = selected || [];
                  const naJustPicked =
                    picked.some((option) => option.value === NA_VALUE) &&
                    !formik.values[field.naName];
                  formik.setFieldValue(field.naName, naJustPicked);
                  formik.setFieldValue(
                    field.name,
                    naJustPicked
                      ? []
                      : picked
                          .filter((option) => option.value !== NA_VALUE)
                          .map((option) => option.value)
                  );
                }}
                name={field.name}
                isMulti={true}
              />
            </label>
          ))}
        </Form>
        <p className="px-5 text-sm text-gray-500">
          Body Type, Hub In-Charge, Fleet Manager, Cluster Manager and Zonal
          Manager are saved on this hub ({data.hubName}) only — other hubs of{' '}
          {data.fleetName} are not affected.
        </p>
        <div className="px-5 flex justify-end items-center gap-4 py-6 flex-wrap sm:w-full">
          <Button bgSecondary onClick={handleClick}>
            Cancel
          </Button>
          <Button
            type="button"
            bgPrimary
            disabled={formik.isSubmitting}
            onClick={formik.handleSubmit}
          >
            Save
          </Button>
        </div>
      </FormikProvider>
    </Modal>
  );
};

export default Edit;
